export const PRODUCT_GUIDE_COPY = {
  en: {
    eyebrow: 'Product guide',
    skip: 'Skip tour',
    back: 'Back',
    next: 'Next',
    finish: 'Finish',
    reopen: 'Open product guide',
    steps: [
      {
        key: 'welcome',
        icon: 'sparkles',
        title: 'Welcome to Linirio',
        description: 'Linirio brings business conversations, AI assistance, approvals, follow-ups, and scheduling into one controlled workspace.',
        points: [
          'Your inbox stays the source of truth.',
          'AI helps prepare and organize work, while you keep control.',
          'Tasks, approvals, posts, and meetings remain visible and auditable.',
        ],
      },
      {
        key: 'security',
        icon: 'shield',
        title: 'Secure your account',
        description: 'Use a unique password and enable two-factor authentication, especially for accounts connected to client communication.',
        points: [
          'Never reuse a shared or temporary password.',
          'Store recovery codes somewhere safe.',
          'Review access immediately when a team member leaves.',
        ],
        cta: { label: 'Open security settings', to: '/app/settings#security' },
      },
      {
        key: 'knowledge',
        icon: 'database',
        title: 'Build your Knowledge Base',
        description: 'The Knowledge Base tells AI what your company actually offers, how it should communicate, and where its limits are.',
        points: [
          'Add services, pricing rules, FAQs, and approved wording.',
          'Keep facts concrete and review them when your offer changes.',
          'If the answer is not supported, AI should ask for human help instead of guessing.',
        ],
        cta: { label: 'Open Knowledge Base', to: '/app/settings#knowledge-base' },
      },
      {
        key: 'linkedin',
        icon: 'linkedin',
        title: 'Connect LinkedIn and your Inbox',
        description: 'Each client connects their own LinkedIn account. Linirio then synchronizes conversations into the shared workflow.',
        points: [
          'Connect through the secure hosted authorization flow.',
          'Use Inbox for replies, context, and conversation history.',
          'Reconnect promptly if synchronization reports an expired session.',
        ],
        cta: { label: 'Connect LinkedIn', to: '/app/settings#linkedin-accounts' },
      },
      {
        key: 'ai',
        icon: 'bot',
        title: 'Configure AI Auto Reply',
        description: 'Auto Reply watches eligible conversations and prepares or sends responses according to your safeguards.',
        points: [
          'Start with approval required while you tune the setup.',
          'Use a quiet window so AI waits for the sender to finish.',
          'Choose tone, confidence limits, working hours, and fallback behavior carefully.',
        ],
        cta: { label: 'Open AI settings', to: '/app/settings#ai-auto-reply' },
      },
      {
        key: 'outreach',
        icon: 'approval',
        title: 'Use Outreach and Approvals safely',
        description: 'Outreach starts conversations; Approvals keep sensitive replies and actions under human control.',
        points: [
          'Begin with conservative daily limits and realistic delays.',
          'Review low-confidence or high-impact messages before sending.',
          'Never enable broad automation before testing with a small lead set.',
        ],
        cta: { label: 'Review outreach settings', to: '/app/settings#auto-outreach' },
      },
      {
        key: 'posts',
        icon: 'file',
        title: 'Create and approve posts',
        description: 'Draft LinkedIn posts in Linirio, review the final copy and media, then publish through the connected account.',
        points: [
          'Treat AI output as a draft, not a finished claim.',
          'Verify names, links, facts, and media before approval.',
          'Use the approval history to see who changed or published a post.',
        ],
        cta: { label: 'Open Posts', to: '/app/posts' },
      },
      {
        key: 'calendar',
        icon: 'calendar',
        title: 'Connect Calendar and start carefully',
        description: 'Each client connects their own Google Calendar. Linirio can create bookings, calendar events, and Google Meet links in that account.',
        points: [
          'Select the correct writable calendar before creating bookings.',
          'Test one meeting from creation through reschedule and cancellation.',
          'Start with Knowledge Base, LinkedIn, approvals, and Calendar before enabling more automation.',
        ],
      },
    ],
  },
  cs: {
    eyebrow: 'Průvodce produktem',
    skip: 'Přeskočit průvodce',
    back: 'Zpět',
    next: 'Další',
    finish: 'Dokončit',
    reopen: 'Otevřít průvodce produktem',
    steps: [
      {
        key: 'welcome',
        icon: 'sparkles',
        title: 'Vítejte v Linirio',
        description: 'Linirio spojuje obchodní konverzace, pomoc AI, schvalování, navazující kroky a plánování do jednoho řízeného pracovního prostoru.',
        points: [
          'Vaše schránka zůstává hlavním zdrojem pravdy.',
          'AI pomáhá připravovat a organizovat práci, kontrolu však máte stále vy.',
          'Úkoly, schvalování, příspěvky a schůzky zůstávají přehledné a dohledatelné.',
        ],
      },
      {
        key: 'security',
        icon: 'shield',
        title: 'Zabezpečte svůj účet',
        description: 'Používejte jedinečné heslo a zapněte dvoufaktorové ověření, zejména u účtů propojených s klientskou komunikací.',
        points: [
          'Nikdy znovu nepoužívejte sdílené nebo dočasné heslo.',
          'Obnovovací kódy uložte na bezpečné místo.',
          'Při odchodu člena týmu ihned zkontrolujte jeho přístupy.',
        ],
        cta: { label: 'Otevřít nastavení zabezpečení', to: '/app/settings#security' },
      },
      {
        key: 'knowledge',
        icon: 'database',
        title: 'Vytvořte znalostní bázi',
        description: 'Znalostní báze říká AI, co vaše společnost skutečně nabízí, jak má komunikovat a kde jsou její hranice.',
        points: [
          'Přidejte služby, cenová pravidla, FAQ a schválené formulace.',
          'Používejte konkrétní fakta a kontrolujte je při změně nabídky.',
          'Pokud odpověď není podložená, AI má požádat o pomoc člověka, nikoli hádat.',
        ],
        cta: { label: 'Otevřít znalostní bázi', to: '/app/settings#knowledge-base' },
      },
      {
        key: 'linkedin',
        icon: 'linkedin',
        title: 'Připojte LinkedIn a schránku',
        description: 'Každý klient připojí svůj vlastní LinkedIn účet. Linirio poté synchronizuje konverzace do společného workflow.',
        points: [
          'Připojte účet prostřednictvím zabezpečeného autorizačního procesu.',
          'Ve schránce pracujte s odpověďmi, kontextem a historií konverzace.',
          'Pokud synchronizace hlásí expirovanou relaci, účet co nejdříve znovu připojte.',
        ],
        cta: { label: 'Připojit LinkedIn', to: '/app/settings#linkedin-accounts' },
      },
      {
        key: 'ai',
        icon: 'bot',
        title: 'Nastavte automatické odpovědi AI',
        description: 'Automatické odpovědi sledují vhodné konverzace a podle vašich bezpečnostních pravidel připravují nebo odesílají reakce.',
        points: [
          'Začněte s povinným schválením, dokud nastavení nevyladíte.',
          'Použijte klidové okno, aby AI počkala, než odesílatel dopíše.',
          'Pečlivě nastavte tón, hranici jistoty, pracovní dobu a náhradní chování.',
        ],
        cta: { label: 'Otevřít nastavení AI', to: '/app/settings#ai-auto-reply' },
      },
      {
        key: 'outreach',
        icon: 'approval',
        title: 'Používejte outreach a schvalování bezpečně',
        description: 'Outreach zahajuje konverzace; schvalování ponechává citlivé odpovědi a akce pod lidskou kontrolou.',
        points: [
          'Začněte s konzervativními denními limity a realistickými prodlevami.',
          'Zprávy s nízkou jistotou nebo vysokým dopadem před odesláním zkontrolujte.',
          'Širokou automatizaci nezapínejte před testem na malé skupině leadů.',
        ],
        cta: { label: 'Zkontrolovat nastavení outreach', to: '/app/settings#auto-outreach' },
      },
      {
        key: 'posts',
        icon: 'file',
        title: 'Vytvářejte a schvalujte příspěvky',
        description: 'V Linirio připravíte LinkedIn příspěvek, zkontrolujete finální text a média a poté jej publikujete přes připojený účet.',
        points: [
          'Výstup AI považujte za návrh, nikoli za hotové tvrzení.',
          'Před schválením ověřte jména, odkazy, fakta a média.',
          'V historii schvalování uvidíte, kdo příspěvek změnil nebo publikoval.',
        ],
        cta: { label: 'Otevřít příspěvky', to: '/app/posts' },
      },
      {
        key: 'calendar',
        icon: 'calendar',
        title: 'Připojte kalendář a začněte opatrně',
        description: 'Každý klient připojí svůj vlastní Google Kalendář. Linirio v něm může vytvářet rezervace, události a odkazy Google Meet.',
        points: [
          'Před vytvořením rezervací vyberte správný kalendář s možností zápisu.',
          'Otestujte jednu schůzku od vytvoření přes přeplánování až po zrušení.',
          'Před širší automatizací začněte znalostní bází, LinkedInem, schvalováním a kalendářem.',
        ],
      },
    ],
  },
};

export const DEFAULT_PRODUCT_GUIDE_LOCALE = 'en';
